/**
 * RTSP URL formats by camera brand, for /rtsp-url and its generator.
 *
 * Every path below comes from the manufacturer's own documentation, linked in
 * `source` (checked 2026-10-09). A brand whose format could not be confirmed
 * there (Bosch live view, Imou, GeoVision) is left out rather than guessed:
 * a wrong path costs an installer an hour on site.
 */

export type RtspStream = 'main' | 'sub' | 'third';

export interface RtspBrand {
  id: string;
  name: string;
  /** Default RTSP port. */
  port: number;
  /** Path per stream. `{ch}` is the 1-based channel number. */
  streams: Partial<Record<RtspStream, string>>;
  /** How a channel other than 1 is addressed, when the vendor documents it. */
  channel?: 'path' | 'query';
  /** Fixed user name or a hint about the credentials. */
  credentials?: string;
  notes: string[];
  source: { label: string; url: string };
}

export const RTSP_BRANDS: readonly RtspBrand[] = [
  {
    id: 'hikvision',
    name: 'Hikvision',
    port: 554,
    streams: { main: '/Streaming/Channels/{ch}01', sub: '/Streaming/Channels/{ch}02' },
    channel: 'path',
    notes: [
      'The last two digits pick the stream: 01 is the main stream, 02 the sub stream.',
      'On an NVR the channel number comes first: channel 3 main stream is /Streaming/Channels/301.',
    ],
    source: { label: 'Hikvision support: How do I get my RTSP stream?', url: 'https://supportusa.hikvision.com/support/solutions/articles/17000129064-how-do-i-get-my-rtsp-stream-' },
  },
  {
    id: 'dahua',
    name: 'Dahua',
    port: 554,
    streams: {
      main: '/cam/realmonitor?channel={ch}&subtype=0',
      sub: '/cam/realmonitor?channel={ch}&subtype=1',
      third: '/cam/realmonitor?channel={ch}&subtype=2',
    },
    channel: 'query',
    notes: [
      'subtype=0 is the main stream, 1 the second stream, 2 the third stream.',
      'Channel numbers start at 1, also on a single camera.',
    ],
    source: { label: 'Dahua Wiki: Remote Access, RTSP via VLC', url: 'https://dahuawiki.com/images/cache/f/fc/Remote_Access%252FRTSP_via_VLC.html' },
  },
  {
    id: 'axis',
    name: 'Axis',
    port: 554,
    streams: { main: '/axis-media/media.amp' },
    notes: [
      'Without parameters the camera sends the default stream from its Video stream settings.',
      'Parameters pick another stream, for example ?resolution=1280x720 or ?videocodec=h264. On a multi-channel encoder, ?camera=2 selects the second input.',
    ],
    source: { label: 'Axis developer documentation: Video streaming (VAPIX)', url: 'https://developer.axis.com/vapix/network-video/video-streaming/' },
  },
  {
    id: 'reolink',
    name: 'Reolink',
    port: 554,
    streams: { main: '/Preview_01_main', sub: '/Preview_01_sub' },
    notes: [
      'RTSP is switched off by default on most Reolink cameras. Enable it in the port settings first.',
      'Do not use special characters in the camera password.',
      'Older guides use /h264Preview_01_main and /h264Preview_01_sub.',
    ],
    source: { label: 'Reolink support: How to Live View Reolink Cameras via VLC Media Player', url: 'https://support.reolink.com/articles/360007010473-How-to-Live-View-Reolink-Cameras-via-VLC-Media-Player/' },
  },
  {
    id: 'uniview',
    name: 'Uniview (UNV)',
    port: 554,
    streams: { main: '/media/video1', sub: '/media/video2', third: '/media/video3' },
    notes: ['video1 is the main stream, video2 the sub stream, video3 the third stream.'],
    source: { label: "Uniview: How to Get a Uniview Camera's RTSP Stream (PDF)", url: "https://www.uniview.com/res/202310/26/20231026_1890310_How%20to%20Get%20a%20Uniview%20Camera's%20RTSP%20Stream_974039_168459_0.pdf" },
  },
  {
    id: 'hanwha',
    name: 'Hanwha Vision (Wisenet)',
    port: 554,
    streams: { main: '/profile2/media.smp' },
    notes: [
      'The number is the video profile configured in the camera. On many models profile 1 is MJPEG and profile 2 is H.264; check the profile list in the camera before using another number.',
      'Multi-sensor cameras add the sensor first: /0/profile2/media.smp to /3/profile2/media.smp.',
    ],
    source: { label: 'Hanwha Vision support: Camera RTSP URL', url: 'https://support.hanwhavision.com/hc/en-001/articles/47782445700243-Camera-RTSP-URL' },
  },
  {
    id: 'amcrest',
    name: 'Amcrest',
    port: 554,
    streams: { main: '/cam/realmonitor?channel={ch}&subtype=0', sub: '/cam/realmonitor?channel={ch}&subtype=1' },
    channel: 'query',
    notes: ['Same scheme as Dahua: subtype=0 is the main stream, subtype=1 the sub stream.'],
    source: { label: 'Amcrest support: Accessing Amcrest Products Using RTSP', url: 'https://support.amcrest.com/hc/en-us/articles/360052688931-Accessing-Amcrest-Products-Using-RTSP' },
  },
  {
    id: 'lorex',
    name: 'Lorex',
    port: 554,
    streams: { main: '/cam/realmonitor?channel={ch}&subtype=0', sub: '/cam/realmonitor?channel={ch}&subtype=1' },
    channel: 'query',
    notes: ['Supported on selected recorders and IP cameras only. subtype=0 is the main stream, subtype=1 the sub stream.'],
    source: { label: 'Lorex help: Using RTSP with your DVR / NVR', url: 'https://www.lorex.com/blogs/help/ip-cameras-using-real-time-streaming-protocol-rtsp-with-your-dvr-nvr' },
  },
  {
    id: 'ezviz',
    name: 'EZVIZ',
    port: 554,
    streams: { main: '/ch1/main' },
    credentials: 'User name admin. The password is the 6-letter verification code on the camera label.',
    notes: ['Some models use /H.264 instead of /ch1/main.'],
    source: { label: 'EZVIZ support: How to configure RTSP stream', url: 'https://community.ezvizlife.com/hc/en-us/articles/360034827754-How-to-configure-RTSP-stream-' },
  },
  {
    id: 'tapo',
    name: 'TP-Link Tapo',
    port: 554,
    streams: { main: '/stream1', sub: '/stream2' },
    credentials: 'Use the camera account created in the Tapo app (6 to 32 characters), not the Tapo cloud login.',
    notes: ['Dual-lens models: the telephoto lens is /stream6 (high quality) and /stream7 (standard).'],
    source: { label: 'TP-Link: How to View Tapo Camera on PC, NAS, or NVR Using RTSP/ONVIF', url: 'https://www.tp-link.com/us/support/faq/2680/' },
  },
  {
    id: 'vivotek',
    name: 'VIVOTEK',
    port: 554,
    streams: { main: '/live.sdp' },
    notes: ['live.sdp is the default access name of stream 1. Other streams use the access names set in the camera streaming settings.'],
    source: { label: 'VIVOTEK FD9391-EHTV user manual (PDF)', url: 'https://download.vivotek.com/downloadfile/downloads/usersmanuals/fd9391-ehtvmanual_en.pdf' },
  },
  {
    id: 'foscam',
    name: 'Foscam',
    port: 88,
    streams: { main: '/videoMain', sub: '/videoSub' },
    notes: ['On most models the RTSP port is the HTTP port, 88 by default.'],
    source: { label: 'Foscam FAQ: How to get the RTSP stream of Foscam HD cameras?', url: 'https://www.foscam.com/faqs/view.html?id=81' },
  },
  {
    id: 'milesight',
    name: 'Milesight',
    port: 554,
    streams: { main: '/main', sub: '/sub', third: '/third' },
    notes: [],
    source: { label: 'Milesight support: RTSP Stream of Milesight Network Camera, NVR, VMS', url: 'https://support.milesight.com/support/solutions/articles/69000859092-rtsp-stream-of-milesight-network-camera-nvr-vms' },
  },
  {
    id: 'avigilon',
    name: 'Avigilon',
    port: 554,
    streams: { main: '/defaultPrimary?streamType=u', sub: '/defaultSecondary?streamType=u', third: '/defaultTertiary?streamType=u' },
    notes: ['The tertiary stream exists on cameras that support it. H4 Pro cameras do not offer RTSP.'],
    source: { label: 'Avigilon support: Examples of RTSP Stream URI of an Avigilon Single-sensor Camera', url: 'https://support.avigilon.com/s/article/Examples-of-RTSP-Stream-URI-of-an-Avigilon-Single-sensor-Camera?language=en_US' },
  },
  {
    id: 'eufy',
    name: 'eufy',
    port: 554,
    streams: { main: '/live0' },
    credentials: 'Turn on NAS (RTSP Stream) in the eufy app under Camera Settings > Storage. The app shows the full link with its user name and password.',
    notes: ['Meant for NAS and recorder software with RTSP support.'],
    source: { label: 'eufy support: About NAS / RTSP', url: 'https://service.eufy.com/article-description/About-NAS-RTSP' },
  },
];

export const RTSP_STREAM_LABEL: Record<RtspStream, string> = {
  main: 'Main stream',
  sub: 'Sub stream',
  third: 'Third stream',
};

export interface RtspUrlInput {
  ip: string;
  port?: number;
  user?: string;
  password?: string;
  channel?: number;
  stream: RtspStream;
}

/**
 * The RTSP URL for a brand and stream. Credentials are percent-encoded, since
 * an @, : or / in a password otherwise breaks the URL. Returns null when the
 * brand has no documented path for that stream.
 */
export function buildRtspUrl(brand: RtspBrand, input: RtspUrlInput): string | null {
  const template = brand.streams[input.stream];
  if (!template) return null;
  const ch = Math.max(1, Math.floor(input.channel ?? 1));
  let path = template.replace('{ch}', String(ch));
  // Axis addresses another input of a multi-channel device with ?camera=N.
  if (brand.id === 'axis' && ch > 1) path += `${path.includes('?') ? '&' : '?'}camera=${ch}`;
  const host = input.ip.trim() || 'CAMERA_IP';
  const port = input.port && input.port > 0 ? input.port : brand.port;
  const user = input.user?.trim();
  const auth = user
    ? `${encodeURIComponent(user)}${input.password ? `:${encodeURIComponent(input.password)}` : ''}@`
    : '';
  return `rtsp://${auth}${host}:${port}${path}`;
}

export function rtspBrandById(id: string): RtspBrand | undefined {
  return RTSP_BRANDS.find((b) => b.id === id);
}
